import { SheetWithFormComponent } from '@/components/ui/SheetWithFormComponent'
import { useRestClient, RestSchemas } from '@/contexts/QueryProvider'
import { ErrorComponent } from '@/ui-legacy/ErrorComponent'
import { FormComponentLoading } from '@/catalog-apps/components/component-library/FormComponent'
import { zodResolver } from '@hookform/resolvers/zod'
import { useCallback, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'

// Shadcn components
import { MultiSelectComboboxField, SelectOption } from '@/components/form-components/MultiSelectCombobox'
import { Button } from '@/components/shadcn/button'
import { Form } from '@/components/shadcn/form'
import { IconAccountConvertOutline } from '@icons/IconAccountConvertOutline'
import { IconDeleteOutline } from '@icons/IconDeleteOutline'
import clsx from 'clsx'

// Form validation schema
const formSchema = z.object({
  selectedRoleIds: z.array(z.number()).min(1, 'Please select at least one role to add'),
})

export type MaintainSupportTeamRolesForm = z.infer<typeof formSchema>

type MessagesSubscriptionsOrganisationTeam = RestSchemas['Messages.Subscriptions.OrganisationTeam']
type MessagesSubscriptionsOrganisationTeamRole = RestSchemas['Messages.Subscriptions.OrganisationTeamRole']
type MessagesApplicationsApplicationRole = RestSchemas['Messages.Applications.ApplicationRole']

type MaintainSupportTeamRolesFormProps = {
  onSubmit?: () => void
  sheetTrigger?: React.ReactNode
  sheetTriggerTooltip?: string
  sheetTitle?: string
  sheetDescription?: string
  openInSheet?: boolean
  selectedTeam: MessagesSubscriptionsOrganisationTeam
}

export const MaintainSupportTeamRolesForm = (props: MaintainSupportTeamRolesFormProps) => {
  const [sheetOpen, setSheetOpen] = useState(false)

  return props.sheetTrigger || props.openInSheet ? (
    <SheetWithFormComponent
      title={props.sheetTitle}
      description={props.sheetDescription}
      trigger={props.sheetTrigger}
      triggerTooltip={props.sheetTriggerTooltip}
      wide
      isOpen={sheetOpen}
      onOpenChange={setSheetOpen}
    >
      <FormComponent {...props} />
    </SheetWithFormComponent>
  ) : (
    <FormComponent {...props} />
  )
}

const FormComponent = (props: MaintainSupportTeamRolesFormProps) => {
  const appId = props.selectedTeam.applicationId
  const teamId = props.selectedTeam.id

  const {
    getApplicationsApiListApplicationRoles,
    getSubscriptionsApiListTeamRoles,
    putSubscriptionsApiAddTeamRole,
    deleteSubscriptionsApiDeleteTeamRole,
    getApplicationsApiGetApplicationTeamsForUserOrganization,
  } = useRestClient()

  const {
    data: applicationRoles,
    isPending: applicationRolesLoading,
    isError: applicationRolesError,
  } = getApplicationsApiListApplicationRoles.useQuery({ query: { applicationId: appId } }, { enabled: !!appId })

  const teamRolesSelector = useCallback(
    (data: MessagesSubscriptionsOrganisationTeamRole[]) =>
      applicationRoles?.filter((role) => data.map((tRole) => tRole.roleId).includes(role.id)),
    [applicationRoles]
  )

  const {
    data: supportTeamRoles,
    isPending: supportTeamRolesLoading,
    isError: supportTeamRolesError,
  } = getSubscriptionsApiListTeamRoles.useQuery(
    { query: { teamId } },
    {
      select: teamRolesSelector,
      enabled: !!applicationRoles,
    }
  )

  const { mutateAsync: addTeamRole } = putSubscriptionsApiAddTeamRole.useMutation(undefined, {
    onSuccess: () => {
      // Invalidate multiple queries
      getSubscriptionsApiListTeamRoles.invalidateQueries({ parameters: { query: { teamId } } })
      getApplicationsApiGetApplicationTeamsForUserOrganization.invalidateQueries({ parameters: { path: { appId } } })
      toast.success('Team roles successfully added')
      props.onSubmit?.()
    },
    onError: () => {
      toast.error('Error adding team roles')
    },
  })

  const { mutate: deleteTeamRole } = deleteSubscriptionsApiDeleteTeamRole.useMutation(undefined, {
    onSuccess: () => {
      // Invalidate multiple queries
      getSubscriptionsApiListTeamRoles.invalidateQueries({ parameters: { query: { teamId } } })
      getApplicationsApiGetApplicationTeamsForUserOrganization.invalidateQueries({ parameters: { path: { appId } } })
      toast.success('Team role successfully removed')
    },
    onError: () => {
      toast.error('Error removing team role')
    },
  })

  const defaultValues = useMemo(() => {
    return {
      selectedRoleIds: [] as number[],
    }
  }, [])

  const form = useForm<MaintainSupportTeamRolesForm>({
    resolver: zodResolver(formSchema),
    defaultValues,
  })

  // Create role options, filtering out already assigned roles
  const roleOptions: SelectOption<number>[] = useMemo(() => {
    if (!applicationRoles) return []

    // Sort roles by name
    const sortedRoles = [...applicationRoles].sort((a, b) => a.name.localeCompare(b.name))

    // Filter out roles that are already assigned to the team
    return sortedRoles
      .filter((role) => !supportTeamRoles?.some((teamRole) => teamRole.id === role.id))
      .map((role) => ({
        value: role.id,
        label: role.name,
      }))
  }, [applicationRoles, supportTeamRoles])

  const handleSubmit = async (data: MaintainSupportTeamRolesForm) => {
    const promises = data.selectedRoleIds.map((roleId) =>
      addTeamRole({
        body: {
          roleId,
          teamId,
        },
      })
    )
    await Promise.all(promises)
    form.reset() // Reset form after submission
  }

  const handleDeleteRole = (role: MessagesApplicationsApplicationRole) => {
    deleteTeamRole({
      body: {
        roleId: role.id,
        teamId,
      },
    })
  }

  if (applicationRolesLoading || supportTeamRolesLoading) return <FormComponentLoading />
  if (applicationRolesError || supportTeamRolesError) return <ErrorComponent />

  return (
    <>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)}>
          <div className="flex h-full flex-col">
            <div className="space-y-6">
              <MultiSelectComboboxField
                control={form.control}
                name="selectedRoleIds"
                label="Add Team Roles"
                options={roleOptions}
                placeholder="Select team roles to add..."
                searchPlaceholder="Search team roles..."
                emptyMessage="No available team roles found."
              />
            </div>
            <div className="form-action-container">
              <Button type="submit" disabled={roleOptions.length === 0}>
                Add Selected Roles
              </Button>
            </div>
          </div>
        </form>
      </Form>
      {supportTeamRoles && supportTeamRoles.length > 0 && (
        <div className="space-y-4">
          <div className="text-sm font-medium">Current Team Roles:</div>
          <div className="space-y-2">
            {supportTeamRoles.map((role) => (
              <TeamRoleCard
                key={`${role.id}-${role.applicationId}`}
                role={role}
                onDelete={() => handleDeleteRole(role)}
              />
            ))}
          </div>
        </div>
      )}
    </>
  )
}

// Team role card component
const TeamRoleCard = ({ role, onDelete }: { role: MessagesApplicationsApplicationRole; onDelete: () => void }) => {
  return (
    <div className="flex items-center justify-between rounded-lg border bg-background p-3">
      <div className="flex items-center gap-5">
        <IconAccountConvertOutline className={clsx('size-7 text-muted-foreground')} />
        <span className="font-medium">{role.name}</span>
      </div>
      <Button variant="ghost" size="icon" onClick={onDelete}>
        <IconDeleteOutline />
      </Button>
    </div>
  )
}
